import { RootState } from '../store';
import { get } from 'lodash-es';
import { useSelector } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';
import { PUBLISH_ASSETS_BASE_URL } from './Common.env';

// Built with createSelector so it returns the *same* object reference across
// calls as long as the underlying primitive fields haven't changed. The
// previous version built a brand-new object literal on every single call --
// with react-redux v8's useSyncExternalStore-based useSelector, a selector
// that never returns a referentially-stable value looks like "the store
// changed" on every render check, regardless of whether anything this
// selector actually reads changed. This component is used once per guide
// step (via useSelector(currentProfileSelector) in StepDetails), so on a
// flo with n number of steps that meant n components simultaneously re-rendering (and
// re-triggering their media loads) on every single store update anywhere in
// the app, not just ones relevant to the profile.
const avatarColorSelector = (state: RootState) =>
	get(state, 'routerContainer.profile.userProfile.profilePreference.avatarColor');

export const currentProfileSelector = createSelector(
	[
		(state: RootState) => get(state, 'routerContainer.profile.name'),
		(state: RootState) => get(state, 'routerContainer.profile.userId'),
		(state: RootState) => get(state, 'routerContainer.profile.userProfile.profilePicUrl'),
		avatarColorSelector,
		(state: RootState) => get(state, 'routerContainer.profile.email'),
		(state: RootState) => get(state, 'routerContainer.profile.anonymous'),
	],
	(name, userId, profilePicUrl, avatarColor, email, is_anonymous) => ({
		name,
		userId,
		profilePicUrl,
		avatarColor,
		profilePreference: {
			avatarColor,
		},
		email,
		is_anonymous,
	})
);

export const enablePoweredBYSelector = (state: RootState): boolean =>
	!get(state, 'floPage.floSpace.appConfig.hidePoweredByFloik', true);

export const hideTryFloikSelector = (state: RootState): boolean =>
	get(state, 'floPage.floSpace.appConfig.hideTryFloik', true);

export const enableTryFloikSelector =
	(floIndex: number) =>
	(state: RootState): boolean => {
		const hideHeader = get(state, `floPage.flos.${floIndex}.floDetails["hide-header"]`);
		const showTryFloik = hideHeader
			? false
			: get(state, `floPage.flos.${floIndex}.floDetails.internalArticles`, false);
		// hideHeader ? false
		return showTryFloik || !get(state, 'floPage.floSpace.appConfig.hideTryFloik', false);
	};

export const headerBrandImageSelector =
	(floIndex: number) =>
	(state: RootState): string => {
		const internalArticles = get(
			state,
			`floPage.flos.${floIndex}.floDetails.internalArticles`,
			false
		);
		const hideHeaderLogo = get(
			state,
			'floPage.floSpace.branding.headerConfigurationData.hideHeaderLogo',
			false
		);
		if (hideHeaderLogo && !internalArticles) {
			return '';
		}
		return get(
			state,
			'floPage.floSpace.branding.headerConfigurationData.imgPath',
			''
		) as string;
	};

export const watermarkBrandImageSelector =
	(floIndex: number) =>
	(state: RootState): string => {
		const internalArticles = get(
			state,
			`floPage.flos.${floIndex}.floDetails.internalArticles`,
			false
		);
		// const customWatermarkDisabled = get(
		// 	state,
		// 	'floPage.floSpace.branding.watermarkConfig.customWatermarkDisabled',
		// 	false
		// );
		if (!internalArticles) {
			return '';
		}
		return (
			internalArticles
				? `${PUBLISH_ASSETS_BASE_URL}/images/floik-logo.svg`
				: get(
						state,
						`floPage.flos.${floIndex}.floSpace.branding.watermarkConfig.imgPath`,
						''
				  )
		) as string;
	};
